import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Check, Sparkles, Award, Brain, X, ChevronRight, Crown, GraduationCap, Gift, Star } from 'lucide-react';
import Footer from './Footer';
import AdvisorRibbon from './AdvisorRibbon';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { ALL_PLANS } from '../config/plans';
import { usePlanPrices, applyLivePrices } from '../utils/livePrices';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';

const API = process.env.REACT_APP_BACKEND_URL || '';

import SEOHead from './SEOHead';

const PricingPlans = () => {
  const navigate = useNavigate();
  const { user, handleLogin } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [signupData, setSignupData] = useState({
    full_name: '',
    email: '',
    password: '',
    mobile_number: ''
  });

  const calculateTotalPrice = (basePrice) => {
    const gst = basePrice * 0.18;
    const total = basePrice + gst;
    return {
      base: basePrice,
      gst: gst.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleGuestSignup = async () => {
    if (!signupData.full_name || !signupData.email || !signupData.password || !signupData.mobile_number) {
      toast.error('Please fill all fields');
      return;
    }

    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const registrationPayload = {
        email: signupData.email,
        password: signupData.password,
        full_name: signupData.full_name,
        profile: {
          mobile_number: signupData.mobile_number,
          completed_questionnaire: false,
          college_name: "To be filled",
          degree_name: "To be filled",
          study_year: "To be filled"
        }
      };
      
      const registerResponse = await axios.post(`${API}/api/auth/register`, registrationPayload);
      
      if (registerResponse.data.access_token) {
        localStorage.setItem('token', registerResponse.data.access_token);
        
        if (handleLogin) {
          await handleLogin(registerResponse.data.user, registerResponse.data.access_token);
        }
        
        toast.success('Account created! Redirecting to checkout...');
        setShowSignupModal(false);
        setLoading(false);
        
        navigate(`/checkout/${selectedPlan.id}`);
      } else {
        toast.error('Registration failed - no token received');
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
      setLoading(false);
    }
  };

  const handlePayment = async (plan) => {
    // On mobile app: open website in browser with auto-login, no Razorpay inside app
    if (isMobileApp()) {
      await openWebappWithAuth('/pricing');
      return;
    }
    navigate(`/checkout/${plan.id}`);
  };

  // Split plans
  const livePrices = usePlanPrices();
  const allPlans = applyLivePrices(ALL_PLANS, livePrices);
  const plans = allPlans.filter(p => !p.jntuSpecial);
  const jntuPlans = allPlans.filter(p => p.jntuSpecial);

  // Get style for each plan category
  const getCategoryStyle = (plan) => {
    if (['iit-jee', 'neet-ug'].includes(plan.id)) {
      return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', gradient: 'from-blue-700 to-blue-800' };
    }
    if (['gre', 'gmat'].includes(plan.id)) {
      return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', gradient: 'from-green-500 to-emerald-600' };
    }
    if (['medical-global', 'dentist-global', 'pharmacist-global'].includes(plan.id)) {
      return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', gradient: 'from-emerald-500 to-teal-600' };
    }
    if (plan.jntuSpecial) {
      return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', gradient: 'from-purple-500 to-pink-600' };
    }
    return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', gradient: 'from-blue-500 to-indigo-600' };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Exam Prep Plans & Pricing"
        description="Affordable exam preparation plans for NEET, IIT-JEE, USMLE, GRE, GMAT and more. AI-powered study tools, mock tests and career guidance for students."
        path="/pricing"
      />
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-12">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 rounded-full mb-4 sm:mb-6">
              <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              <span className="text-xs sm:text-sm font-bold text-purple-700">PREMIUM EXAM PREPARATION</span>
            </div>
            
            {/* Title */}
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
              Choose Your Success Plan
            </h1>
            
            {/* Subtitle */}
            <p className="text-sm sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto mb-5 sm:mb-8 px-2">
              Premium, comprehensive exam preparation plans designed for your success
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 rounded-full">
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">AI Doubt Solving</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 rounded-full">
                <Star className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">Detailed Explanations</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 rounded-full">
                <Gift className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500" />
                <span className="text-xs sm:text-sm font-medium text-gray-700">Flexible Access</span>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-xs sm:max-w-lg mx-auto">
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{ALL_PLANS.length}</p>
                <p className="text-xs sm:text-sm text-gray-500">Plans</p>
              </div>
              <div className="text-center border-x border-gray-200">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">5+</p>
                <p className="text-xs sm:text-sm text-gray-500">Categories</p>
              </div>
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-purple-500">24/7</p>
                <p className="text-xs sm:text-sm text-gray-500">AI Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {plans.map((plan) => {
            const pricing = calculateTotalPrice(plan.price);
            const style = getCategoryStyle(plan);
            
            return (
              <Card
                key={plan.id}
                className={`group relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 ${style.border}`}
              >
                {/* Card Header gradient */}
                <div className={`h-1.5 sm:h-2 bg-gradient-to-r ${style.gradient}`}></div>
                
                {/* Badges */}
                {plan.popular && (
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                      <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1">
                        <Award className="h-2.5 w-2.5 sm:h-3 sm:w-3 inline mr-1" />
                        Popular
                      </Badge>
                    </div>
                  )}
                  
                  {plan.jntuSpecial && (
                    <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                      <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1">
                        JNTU SPECIAL
                      </Badge>
                    </div>
                  )}

                  <CardContent className="p-4 sm:p-6">
                    {/* Icon and Title */}
                    <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className={`w-10 h-10 sm:w-14 sm:h-14 ${style.bg} rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0`}>
                        {plan.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm sm:text-lg mb-0.5 sm:mb-1">{plan.name}</h3>
                        {plan.subtitle && (
                          <p className="text-[10px] sm:text-xs text-gray-500">{plan.subtitle}</p>
                        )}
                      </div>
                    </div>

                    {/* Duration Badge */}
                    <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {plan.duration}
                      </Badge>
                      <Badge className="text-[10px] sm:text-xs bg-gradient-to-r from-blue-700 to-blue-800 text-white border-0">
                        Limited Offer
                      </Badge>
                    </div>

                    {/* Pricing Box */}
                    <div className={`${style.bg} rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4`}>
                      <div className="flex items-baseline justify-between">
                        <div>
                          <p className={`text-2xl sm:text-3xl font-bold ${style.text}`}>₹{pricing.base}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500">+ 18% GST</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs sm:text-sm font-semibold text-gray-700">₹{pricing.total}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500">Total</p>
                        </div>
                      </div>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-5">
                      {plan.features.map((feature, index) => {
                        const isHighlighted = feature.includes('⭐');
                        return (
                          <li 
                            key={index} 
                            className={`flex items-start text-xs sm:text-sm ${
                              isHighlighted ? 'bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg py-1 px-1' : ''
                            }`}
                          >
                            <Check className={`h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2 mt-0.5 flex-shrink-0 ${
                              isHighlighted ? 'text-orange-600' : 'text-green-500'
                            }`} />
                            <span className={`${isHighlighted ? 'text-gray-900 font-semibold' : 'text-gray-600'}`}>
                              {feature}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handlePayment(plan)}
                      disabled={loading}
                      className={`w-full bg-gradient-to-r ${style.gradient} hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base group-hover:shadow-lg transition-all disabled:opacity-50`}
                    >
                      <span>Register Now</span>
                      <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

      {/* Student Offer Zone Section */}
      {jntuPlans.length > 0 && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-3 sm:mb-4">
              <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              <span className="text-xs sm:text-sm font-bold text-white">STUDENT OFFER ZONE</span>
            </div>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
              Exclusive Plans for JNTU Students
            </h2>
            <p className="text-xs sm:text-sm lg:text-base text-gray-600 max-w-3xl mx-auto px-2">
              Special offers for JNTU & Affiliated College Students & Graduates - Get unlimited access to premium courses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {jntuPlans.map((plan) => {
              const pricing = calculateTotalPrice(plan.price);
              const style = getCategoryStyle(plan);
              
              return (
                <Card
                  key={plan.id}
                  className="group relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 border-purple-200"
                >
                  {/* Card Header gradient */}
                  <div className="h-1.5 sm:h-2 bg-gradient-to-r from-purple-500 to-pink-600"></div>
                  
                  {/* JNTU Badge */}
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1">
                      JNTU SPECIAL
                    </Badge>
                  </div>

                  <CardContent className="p-4 sm:p-6">
                    {/* Icon and Title */}
                    <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-purple-50 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                        {plan.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-0.5 sm:mb-1 leading-tight">{plan.name}</h3>
                        {plan.subtitle && (
                          <p className="text-[10px] sm:text-xs text-gray-500">{plan.subtitle}</p>
                        )}
                      </div>
                    </div>

                    {/* Duration Badge */}
                    <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {plan.duration}
                      </Badge>
                      <Badge className="text-[10px] sm:text-xs bg-gradient-to-r from-blue-700 to-blue-800 text-white border-0">
                        Limited Offer
                      </Badge>
                    </div>

                    {/* Pricing Box */}
                    <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                      <div className="flex items-baseline justify-between">
                        <div>
                          <p className="text-2xl sm:text-3xl font-bold text-purple-700">₹{pricing.base}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500">+ 18% GST</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs sm:text-sm font-semibold text-gray-700">₹{pricing.total}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500">Total</p>
                        </div>
                      </div>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-5">
                      {plan.features.map((feature, index) => {
                        const isHighlighted = feature.includes('⭐');
                        return (
                          <li 
                            key={index} 
                            className={`flex items-start text-xs sm:text-sm ${
                              isHighlighted ? 'bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg py-1 px-1' : ''
                            }`}
                          >
                            <Check className={`h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2 mt-0.5 flex-shrink-0 ${
                              isHighlighted ? 'text-orange-600' : 'text-green-500'
                            }`} />
                            <span className={`${isHighlighted ? 'text-gray-900 font-semibold' : 'text-gray-600'}`}>
                              {feature}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handlePayment(plan)}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base group-hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      <span>Register Now</span>
                      <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Unlimited Access Section */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 rounded-full mb-3 sm:mb-4">
            <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            <span className="text-xs sm:text-sm font-bold text-blue-700">COURSE BUNDLES</span>
          </div>
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
            Unlimited Access for 1 Year
          </h2>
          <p className="text-xs sm:text-sm lg:text-base text-gray-600 max-w-3xl mx-auto px-2">
            All courses taught by top managers, programmers and scientists from MNCs for just ₹3,000 + 18% GST
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Engineering Student Success Plan */}
          <Card className="border-2 border-blue-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="h-1.5 sm:h-2 bg-gradient-to-r from-blue-500 to-cyan-600"></div>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-50 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  ⚙️
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm sm:text-lg">Engineering Student Success Plan</h3>
                  <p className="text-[10px] sm:text-xs text-gray-500">Unlimited Access for 1 year</p>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
                  <span>• Base SAS Programming</span>
                  <span>• Advanced SAS</span>
                  <span>• Clinical SAS</span>
                  <span>• Data Science</span>
                  <span>• Data Engineering</span>
                  <span>• Web Development</span>
                  <span>• Digital Marketing</span>
                  <span>• & many more</span>
                </div>
              </div>
              
              <Button 
                onClick={() => window.open('https://nerdindians.com/engineering-student-success-plan/', '_blank')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base"
              >
                Visit Program Page
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Business & Finance */}
          <Card className="border-2 border-amber-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="h-1.5 sm:h-2 bg-gradient-to-r from-amber-500 to-orange-600"></div>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-amber-50 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  💼
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm sm:text-lg">Business & Finance Success Plan</h3>
                  <p className="text-[10px] sm:text-xs text-gray-500">Unlimited Access for 1 year</p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
                  <span>• Analytics Programming</span>
                  <span>• Business Analytics</span>
                  <span>• Data Science</span>
                  <span>• Web Development</span>
                  <span>• Digital Marketing</span>
                  <span>• HR Residency</span>
                  <span>• Advanced Excel</span>
                </div>
              </div>
              
              <Button 
                onClick={() => window.open('https://nerdindians.com/business-finance-student-success-plan/', '_blank')}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base"
              >
                Visit Program Page
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Pharma Student */}
          <Card className="border-2 border-teal-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="h-1.5 sm:h-2 bg-gradient-to-r from-teal-500 to-green-600"></div>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-teal-50 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  💊
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm sm:text-lg">Pharma Student Success Plan</h3>
                  <p className="text-[10px] sm:text-xs text-gray-500">Unlimited Access for 1 year</p>
                </div>
              </div>
              
              <div className="bg-teal-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
                  <span>• Base & Advanced SAS</span>
                  <span>• Clinical SAS</span>
                  <span>• Clinical Data Mgmt</span>
                  <span>• Clinical Research</span>
                  <span>• Pharmacovigilance</span>
                  <span>• Medical Coding</span>
                  <span>• Regulatory Affairs</span>
                  <span>• AI for Medicine</span>
                </div>
              </div>
              
              <Button 
                onClick={() => window.open('https://nerdindians.com/pharma-life-sciences-student-success-plan/', '_blank')}
                className="w-full bg-gradient-to-r from-teal-500 to-green-600 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base"
              >
                Visit Program Page
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Life-Sciences */}
          <Card className="border-2 border-emerald-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="h-1.5 sm:h-2 bg-gradient-to-r from-emerald-500 to-lime-600"></div>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-emerald-50 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  🧬
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm sm:text-lg">Life-Sciences Success Plan</h3>
                  <p className="text-[10px] sm:text-xs text-gray-500">Unlimited Access for 1 year</p>
                </div>
              </div>
              
              <div className="bg-emerald-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Includes:</p>
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
                  <span>• Base & Advanced SAS</span>
                  <span>• Clinical SAS</span>
                  <span>• Clinical Data Mgmt</span>
                  <span>• Clinical Research</span>
                  <span>• Medical Coding</span>
                  <span>• AI for Medicine</span>
                  <span>• Genomics Education*</span>
                </div>
              </div>
              
              <Button 
                onClick={() => window.open('https://nerdindians.com/pharma-life-sciences-student-success-plan/', '_blank')}
                className="w-full bg-gradient-to-r from-emerald-500 to-lime-600 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base"
              >
                Visit Program Page
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Why Choose Section */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-purple-100 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                <Brain className="h-5 w-5 sm:h-7 sm:w-7 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base sm:text-xl mb-1 sm:mb-2">
                  Why Choose Nerd AI?
                </h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-700 leading-relaxed">
                  All plans include access to our AI-powered doubt-solving buddy, comprehensive MCQ banks, 
                  and flexible learning schedules. Study at your own pace, anytime, anywhere!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom CTA */}
      <div className="bg-gray-900 mt-8 sm:mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="text-center">
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-sm sm:text-lg text-gray-400 mb-5 sm:mb-8 max-w-2xl mx-auto px-2">
              Join thousands of students who have achieved their dreams with Nerd AI
            </p>
            <Button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="golden-shine text-white font-bold px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-lg rounded-lg sm:rounded-xl hover:shadow-lg transition-all"
            >
              Choose Your Plan
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 ml-1.5 sm:ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tax Info */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 text-center">
        <p className="text-[10px] sm:text-xs text-gray-500">
          * All prices are in INR. GST (18%) will be added to the base price. Secure payment via Razorpay.
        </p>
      </div>

      <div className="mb-20 sm:mb-24"></div>
      <AdvisorRibbon />
      <Footer />

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Create Account</h2>
              <button
                onClick={() => setShowSignupModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-xs sm:text-sm text-gray-600 mb-4">
              Create an account to proceed with your {selectedPlan?.name} purchase
            </p>

            <div className="space-y-3 sm:space-y-4">
              <Input
                type="text"
                placeholder="Full Name"
                value={signupData.full_name}
                onChange={(e) => setSignupData({...signupData, full_name: e.target.value})}
                className="text-sm sm:text-base"
              />
              <Input
                type="email"
                placeholder="Email"
                value={signupData.email}
                onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                className="text-sm sm:text-base"
              />
              <Input
                type="password"
                placeholder="Password (min 6 characters)"
                value={signupData.password}
                onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                className="text-sm sm:text-base"
              />
              <Input
                type="tel"
                placeholder="Mobile Number"
                value={signupData.mobile_number}
                onChange={(e) => setSignupData({...signupData, mobile_number: e.target.value})}
                className="text-sm sm:text-base"
              />
            </div>

            <div className="flex gap-3 mt-5 sm:mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSignupModal(false)}
                className="flex-1 text-sm sm:text-base"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGuestSignup}
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-sm sm:text-base"
              >
                {loading ? 'Creating...' : 'Create & Pay'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPlans;
